import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { STALE_TIMES, GC_TIMES } from '@ais-ui/custom-hooks';
import { useSession } from '../SessionProvider';
import {
  getOverlayMetadata,
  getOverlayMP,
  GetOverlayProps,
} from './getOverlay';
import { useState } from 'react';
import { Base64String } from '../utils';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useRefetchOnSocketChange } from '../useRefetchOnSocketChange';

export const useGetOverlayMP = (
  props: GetOverlayProps | null,
): UseQueryResult<
  {
    overlay: Base64String;
    overlayCreatedDatetime: string;
    targetCountInfo: string;
    timeUntilNextUpdate: number;
  },
  unknown
> => {
  const { olSession, sessionId } = useSession();
  const { timeZone } = useTimeZoneContext();
  const [updateInterval, setUpdateInterval] = useState(5000);

  const query = useQuery(
    {
      queryKey: [
        'getOverlay',
        // Keyed on the stable `sessionId`, never the per-reconnect socketId:
        // each overlay frame is a decoded PNG plus a CPU-backed canvas
        // (~15-30 MB), so a new key per reconnect accumulates frames instead of
        // replacing them. `useRefetchOnSocketChange` still forces a refetch
        // against the reconnected socket (the grace window serves the
        // last-known-good snapshot — see SessionProvider).
        sessionId,
        props?.centerLon,
        props?.centerLat,
        props?.zoomLevel,
        props?.height,
        props?.width,
        props?.leftLon,
        props?.rightLon,
        timeZone,
      ],
      queryFn: async () => {
        const result = await getOverlayMP(olSession, props!, timeZone);
        const metadata = await getOverlayMetadata(olSession);
        setUpdateInterval(metadata.timeUntilNextUpdate);

        // Merge overlay result with metadata to include all keys
        return {
          ...result,
          ...metadata,
        };
      },
      enabled: props !== null,
      staleTime: STALE_TIMES.REALTIME,
      refetchInterval: updateInterval,
      gcTime: GC_TIMES.NONE,
      retry: 3,
      retryDelay: 1000,
    },
    'overlay:get',
  );

  useRefetchOnSocketChange(olSession?.socketId, props !== null, query.refetch);

  return query;
};
